import React, { useEffect, useState, useCallback } from 'react';
import { Modal } from 'antd';
import Chip from '@mui/material/Chip';
import Notification from '../../Notification/Notification';
import Media from '../MediaComponent/MediaComponent';
import FilterContainer from '../Filter/FilterContainer'; // Import FilterContainer
import './AssignCategory.css';
import { fetchMedia } from '../../../services/mediaService'; // Import the new service
import { fetchCategories } from '../../../services/categoryService'; // Import category service

const apiUrl = process.env.REACT_APP_API_URL || '';

function buildPaths(item) {
  const hasFilename = item.filename && item.upload_time;
  const year = hasFilename ? new Date(item.upload_time * 1000).getFullYear() : null;
  const month = hasFilename ? (new Date(item.upload_time * 1000).getMonth() + 1) : null;

  const thumbRelative = item.thumbnailUrl || (hasFilename ? `/uploads/${item.image_type}s/${year}/${month}/thumb-${item.filename}` : item.url || '');
  const originalRelative = hasFilename ? `/uploads/${item.image_type}s/${year}/${month}/${item.filename}` : (item.url || '');

  const normalize = (p) => {
    if (!p) return '';
    if (p.startsWith('http') || p.startsWith('data:')) return p;
    if (p.startsWith('/')) return `${apiUrl}${p}`;
    return p;
  };

  return {
    thumbnailUrl: normalize(thumbRelative),
    originalUrl: normalize(originalRelative),
  };
}

function AssignCategory() {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const token = localStorage.getItem('token') || '';
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [columns, setColumns] = useState(3);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortOrder, setSortOrder] = useState('newest');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalSelectedIds, setModalSelectedIds] = useState([]);
  const [modalSelectedCategories, setModalSelectedCategories] = useState([]);
  const [parentCategories, setParentCategories] = useState([]);
  const [childCategories, setChildCategories] = useState([]);
  const [modalParentCategory, setModalParentCategory] = useState({});
  const [assignInProgress, setAssignInProgress] = useState(false);
  const [processingIds, setProcessingIds] = useState(new Set());
  const [filters, setFilters] = useState({}); // State to hold current filters
  const [categories, setCategories] = useState([]); // Kategóriák tárolása

  const fetchCats = useCallback(async () => { // Wrap fetchCategories in useCallback
    try {
      const normalizedData = await fetchCategories(token); // Pass token to fetchCategories
      setCategories(normalizedData);
      setError(null);
      setSuccess('Kategóriák betöltve');
    } catch (err) {
      setError(err.message);
    }
  }, [token]); // Dependencies for useCallback

  const fetchImages = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const normalized = await fetchMedia(token, sortOrder, filters); // Pass filters to fetchMedia
      setItems(normalized);
      setFilteredData(normalized);
      setCurrentPage(1);
    } catch (err) {
      setError(err && err.message ? err.message : 'Failed to load images');
    } finally {
      setLoading(false);
    }
  }, [sortOrder, token, filters]); // Add filters to dependencies

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  useEffect(() => {
    fetchCats();
  }, [fetchCats]);

  function toggleSelect(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function selectAllVisible() {
    const paginated = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);
    const ids = paginated.map((i) => i.id);
    setSelected(new Set(ids));
  }

  function clearSelection() {
    setSelected(new Set());
  }

  const handleFilter = (newFilters) => {
    setFilters(newFilters); // Update the filters state
    setCurrentPage(1); // Reset to the first page when filters change
  };

  const handleSortChange = (value) => {
    setSortOrder(value);
    const sorted = [...filteredData].sort((a, b) => {
      if (value === 'newest') return new Date(b.uploadDate) - new Date(a.uploadDate);
      if (value === 'oldest') return new Date(a.uploadDate) - new Date(b.uploadDate);
      if (value === 'name') return (a.name || '').localeCompare(b.name || '');
      if (value === 'category') return ('' + (a.category || '')).localeCompare('' + (b.category || ''));
      return 0;
    });
    setFilteredData(sorted);
    setCurrentPage(1);
  };

  const handlePageChange = (page, size) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const handlePageSizeChange = (value) => {
    setPageSize(value);
    setCurrentPage(1);
  };

  const handleColumnsChange = (value) => {
    setColumns(value);
  };

  async function assignSelectedCategory() {
    // open modal for bulk assignment
    if (selected.size === 0) {
      setError('No items selected');
      return;
    }
    setModalSelectedIds(Array.from(selected));
    setModalSelectedCategories([]);
    setIsModalVisible(true);
  }

  async function assignCategoriesToItems(ids = [], categoryIds = []) {
    if (!ids.length || !categoryIds.length) {
      setError(!ids.length ? 'No items to assign' : 'No categories selected');
      return false;
    }

    const categoriesValue = Array.isArray(categoryIds) ? categoryIds.join(',') : String(categoryIds);
    const prevItemsMap = new Map();
    setAssignInProgress(true);
    setProcessingIds(new Set(ids));

    // Optimistic update egyszerre
    setItems((prev) => prev.map(it => {
      if (ids.includes(it.id)) {
        prevItemsMap.set(it.id, it);
        return {
          ...it,
          categories: Array.isArray(categoryIds) ? [...categoryIds] : String(categoryIds).split(','),
          category: Array.isArray(categoryIds) ? categoryIds[0] : String(categoryIds).split(',')[0] || ''
        };
      }
      return it;
    }));
    setFilteredData((prev) => prev.map(it => prevItemsMap.has(it.id) ? prevItemsMap.get(it.id) : it));

    try {
      const res = await fetch(`${apiUrl}/images/updateCategories`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ categories: categoriesValue, image_ids: ids }),
      });

      if (!res.ok) throw new Error(`Failed to update categories (${res.status})`);

      // sikeres: csak egy update
      setItems(prev => prev.map(it => ids.includes(it.id) ? { ...it, categories: categoryIds, category: categoryIds[0] || '' } : it));
      setFilteredData(prev => prev.map(it => ids.includes(it.id) ? { ...it, categories: categoryIds, category: categoryIds[0] || '' } : it));
      setSuccess(`Assigned ${categoryIds.length} category(s) to ${ids.length} item(s)`);
      setSelected(new Set());
      return true;

    } catch (err) {
      // rollback
      setItems(prev => prev.map(it => prevItemsMap.has(it.id) ? prevItemsMap.get(it.id) : it));
      setFilteredData(prev => prev.map(it => prevItemsMap.has(it.id) ? prevItemsMap.get(it.id) : it));
      setError(err.message || 'Category update failed');
      return false;

    } finally {
      setAssignInProgress(false);
      setProcessingIds(new Set());
    }
  }

  function handleOpenAssignModalForSingle(itemId) {
    // try to prefill existing categories if available
    const item = items.find((it) => it.id === itemId);
    let preParent = {}
    let preChildren = [];
    const itemCategories = item.categories.split(',').map(Number) || [];

    if (item && itemCategories && Array.isArray(itemCategories) && itemCategories.length > 0) {

      for (const cId of itemCategories) {
        preParent = parentCategories.find((cat) => (cat.id === cId && cat.is_parent));
        if (preParent) break;
      }
      preChildren = preParent ? itemCategories.filter((c) => c !== (preParent.id)) : itemCategories;

      setModalSelectedIds([itemId]);
      setModalSelectedCategories(preChildren);
      setModalParentCategory(preParent.id);
      setIsModalVisible(true);
    }
  }

  useEffect(() => {
    // derive parent categories from passed categories propcons

    setParentCategories((categories || []).filter((c) => c.is_parent));
    setChildCategories((categories || []).filter((c) => !c.is_parent));

  }, [categories]);

  const handleModalParentChange = (e) => {
    const parentId = e.target.value ? (isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value)) : '';
    setModalParentCategory(parentId);

  };

  const handleModalChipClick = (categoryId) => {
    setModalSelectedCategories((prev) => {
      if (prev.includes(categoryId)) return prev.filter((id) => id !== categoryId);
      return [...prev, categoryId];
    });
  };

  function handleModalCancel() {
    setIsModalVisible(false);
    setModalSelectedIds([]);
    setModalSelectedCategories([]);
  }

  async function handleModalOk() {

    Modal.confirm({
      title: 'Confirm Assignment',
      content: `Are you sure you want to assign the selected categories?`,
      okText: 'Assign',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: async () => {
        setIsModalVisible(false);
        const ids = modalSelectedIds.length ? modalSelectedIds : [];
        // include parent category first if selected
        const catIds = modalSelectedCategories.slice();
        if (modalParentCategory) {
          // ensure parent is included at front
          if (!catIds.includes(modalParentCategory)) catIds.unshift(modalParentCategory);
        }
        setLoading(true);
        const ok = await assignCategoriesToItems(ids, catIds);
        setLoading(false);
        if (ok) {
          // update only affected items by fetching each from server
          try {
            const updatedItems = [];
            for (const id of ids) {
              // eslint-disable-next-line no-await-in-loop
              const res = await fetch(`${apiUrl}/images/${id}`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
              });
              if (res.ok) {
                // eslint-disable-next-line no-await-in-loop
                const json = await res.json();
                const { thumbnailUrl, originalUrl } = buildPaths(json);
                updatedItems.push({ ...json, name: json.originalname || json.name || json.filename || 'unnamed', uploadDate: json.upload_time ? new Date(json.upload_time * 1000).toISOString() : json.uploadDate || '', thumbnailUrl, originalUrl });
              }
            }

            if (updatedItems.length > 0) {
              setItems((prev) => prev.map((it) => {
                const found = updatedItems.find((u) => u.id === it.id);
                return found ? found : it;
              }));
              setFilteredData((prev) => prev.map((it) => {
                const found = updatedItems.find((u) => u.id === it.id);
                return found ? found : it;
              }));
            }
          } catch (e) {
            // fallback to best-effort local update
            setItems((prev) => prev.map((it) => (ids.includes(it.id) ? { ...it, categories: catIds, category: catIds[0] || '' } : it)));
            setFilteredData((prev) => prev.map((it) => (ids.includes(it.id) ? { ...it, categories: catIds, category: catIds[0] || '' } : it)));
          }

          setSelected(new Set());
          setSuccess(`Assigned ${catIds.length} category(s) to ${ids.length} item(s)`);
        }
        setModalSelectedIds([]);
        setModalSelectedCategories([]);
        setModalParentCategory('');

      }
    });

  }

  const paginatedData = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div className="delete-container">
      <FilterContainer
        onFilter={handleFilter}
        onPageSizeChange={handlePageSizeChange}
        onColumnsChange={handleColumnsChange}
        onSortChange={handleSortChange}
        currentPage={currentPage}
        pageSize={pageSize}
        sortOrder={sortOrder}
        columns={columns}
        totalItems={filteredData.length}
        onPageChange={handlePageChange}
        categories={categories}
      />

      <div className="dm-action-buttons-container"> {/* New container for action buttons */}
        <div className="dm-control-item">
          <button className="dm-btn" onClick={selectAllVisible}>
            Select all
          </button>
        </div>
        <div className="dm-control-item">
          <button className="dm-btn" onClick={clearSelection}>
            Clear
          </button>
        </div>
        <div className="dm-control-item">
          <button
            className={`dm-btn ${selected.size ? 'dm-btn-category' : ''}`}
            onClick={assignSelectedCategory}
            disabled={selected.size === 0}
            style={{ marginLeft: 8 }}
          >
            Set category ({selected.size})
          </button>
        </div>
        <div className="dm-control-item">
          {loading && <span style={{ marginLeft: 8 }}>Loading...</span>}
        </div>
      </div>

      <div className="dm-grid" style={{ gridTemplateColumns: `repeat(${columns}, 1fr)` }}>
        {paginatedData.length === 0 && !loading && <div>No media found.</div>}

        {paginatedData.map((item, idx) => {
          // Add more robust checks for item and item.id
          if (!item || item.id === undefined || item.id === null) {
            console.error('AssignCategory: Skipping item due to missing ID:', item); // More specific log
            return null; // Skip rendering this item if it's invalid
          }
          const indexInFiltered = (currentPage - 1) * pageSize + idx;
          const name = item.originalname || item.name || item.filename || 'unnamed';

          const isItemSelected = (selected instanceof Set && item && item.id !== undefined && item.id !== null)
            ? selected.has(item.id)
            : false;

          return (
            <div key={item.id} className="dm-item">
              <input
                className="dm-checkbox"
                type="checkbox"
                checked={isItemSelected}
                onChange={() => toggleSelect(item.id)}
                aria-label={`Select ${name}`}
              />

              <Media categories={categories} size={160} currentIndex={indexInFiltered} data={filteredData} />

              <div className="dm-thumb-wrapper" style={{ position: 'relative' }}>
                {processingIds instanceof Set && item && item.id !== undefined && item.id !== null && processingIds.has(item.id) && (
                  <div className="processing-overlay">
                    <div className="spinner" />
                  </div>
                )}
              </div>

              <div className="dm-actions">
                <button className="dm-btn dm-btn-category" onClick={() => handleOpenAssignModalForSingle(item.id)} disabled={assignInProgress}>
                  Set category
                </button>

              </div>
            </div>
          );
        })}
      </div>

      <Modal
        title="Assign categories"
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        okText="OK"
        cancelText="Cancel"
      >
        <p>Először válassz szülú kategóriát, aztán a többit:</p>

        <div style={{ marginBottom: 8 }}>
          <label style={{ display: 'block', marginBottom: 4 }}>Parent category:</label>
          <select value={modalParentCategory} onChange={handleModalParentChange} style={{ width: '100%', padding: '6px' }}>
            <option value="">Select parent category</option>
            {parentCategories.map((c) => (
              <option key={c.id || c.value || c} value={c.id || c.value || c}>
                {c.name || c.label || c}
              </option>
            ))}
          </select>
        </div>

        {modalParentCategory && (
          <div>
            <label style={{ display: 'block', marginBottom: 6 }}>Child categories:</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {childCategories.map((cat) => (
                <Chip
                  key={cat.id}
                  label={cat.name}
                  onClick={() => handleModalChipClick(cat.id)}
                  style={{
                    backgroundColor: modalSelectedCategories.includes(cat.id) ? '#1976d2' : '#e0e0e0',
                    color: modalSelectedCategories.includes(cat.id) ? '#fff' : '#000',
                    cursor: 'pointer',
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </Modal>

      <Notification
        message={error || success}
        type={error ? 'error' : success ? 'success' : ''}
        onClose={() => {
          setError('');
          setSuccess('');
        }}
      />
    </div>
  );
}

export default AssignCategory;
